import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Check, Cookie } from '@/components/icons';
import { Button } from '@/components/common/Button';
import { Switch } from '@/components/common/Switch';
import { Tooltip } from '@/components/common/Tooltip';
import { applyConsentToGtag, loadConsent, saveConsent, trackPageView } from '@/lib/consent';

/**
 * Analytics consent gate.
 *
 * Renders until the visitor records a choice. Accepting grants
 * `analytics_storage`; rejecting leaves the tag denied. Advertising signals
 * stay denied permanently, so the only switch shown is analytics.
 */
export const ConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const [decided, setDecided] = useState<boolean>(() => loadConsent() !== null);
  const [analytics, setAnalytics] = useState<boolean>(() => loadConsent()?.analytics ?? false);
  const [managing, setManaging] = useState(false);

  const decide = useCallback((value: boolean) => {
    const state = saveConsent(value);
    applyConsentToGtag(state);
    setAnalytics(value);
    setDecided(true);
    setManaging(false);
    // The route effect has already run, so send the current view now if the
    // visitor just opted in while sitting on a measurable page.
    trackPageView(window.location.pathname);
  }, []);

  const reopen = useCallback(() => {
    setManaging(true);
  }, []);

  useEffect(() => {
    if (decided) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setManaging(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [decided, managing]);

  if (decided && !managing) {
    const manageLabel = t('consent.manage', 'Cookie settings');
    return (
      <Tooltip content={manageLabel} side="left">
        <button
          type="button"
          onClick={reopen}
          aria-label={manageLabel}
          title={manageLabel}
          className="fixed bottom-3 right-3 z-40 inline-flex size-9 items-center justify-center rounded-lg border border-border/80 bg-card text-muted-foreground shadow-xs transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Cookie className="h-4 w-4" />
        </button>
      </Tooltip>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('consent.title', 'Cookie settings')}
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 sm:inset-x-auto sm:right-0 sm:justify-end sm:p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-border/80 bg-card p-4 text-foreground shadow-lg sm:p-5">
        <div className="flex items-start gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Cookie className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold tracking-tight text-foreground">
              {t('consent.title', 'Cookie settings')}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t(
                'consent.description',
                'We use Google Analytics to understand how the public marketing page is used. It is off until you accept it, and you can change your choice at any time.'
              )}
            </p>
            {managing && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border/80 bg-background px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground">
                    {t('consent.analytics_label', 'Analytics cookies')}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t('consent.analytics_help', 'Google Analytics, used to measure site usage.')}
                  </p>
                </div>
                <Switch
                  id="consent-analytics"
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  aria-label={t('consent.analytics_label', 'Analytics cookies')}
                />
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              {managing ? (
                <>
                  <Button type="button" variant="outline" onClick={() => setManaging(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    leftIcon={<Check className="w-4 h-4" />}
                    onClick={() => decide(analytics)}
                  >
                    {t('consent.save', 'Save choices')}
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => decide(false)}>
                    {t('consent.reject', 'Reject')}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    leftIcon={<BarChart3 className="w-4 h-4" />}
                    onClick={() => decide(true)}
                  >
                    {t('consent.accept', 'Accept analytics')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
